import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, Stack, Typography } from "@mui/material";
import { ExpandMore as ExpandMoreIcon, ErrorOutline as ErrorIcon } from "@mui/icons-material";
import type { DocumentLoad } from "@/types";

interface FailedRecordsAccordionProps {
  /** Failed records for THIS schema only — filter at the call site. */
  failedRecords: NonNullable<DocumentLoad["failed_records"]>;
  /** Label shown in the accordion header (e.g. "Claimants"). */
  schemaLabel: string;
}

/**
 * Per-schema collapsible accordion for failed records.
 *
 * Auto-expanded when any failure exists for the schema — reviewers should see
 * the error inline rather than have to discover it behind a click. Returns
 * `null` when the schema has no failures, so callers don't have to guard.
 *
 * Replaces the global `FailedRecordsSection` in the legacy
 * `DocumentLoadedRecordsTab`. Splitting per-schema keeps the failure context
 * adjacent to the loaded records for the same schema instead of bottoming
 * out on a separate section.
 */
export const FailedRecordsAccordion = ({ failedRecords, schemaLabel }: FailedRecordsAccordionProps) => {
  if (failedRecords.length === 0) return null;

  return (
    <Accordion
      defaultExpanded
      disableGutters
      elevation={0}
      sx={{
        bgcolor: "transparent",
        border: 1,
        borderColor: "error.light",
        borderRadius: 1,
        mt: 1.5,
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ minHeight: 40, "& .MuiAccordionSummary-content": { my: 0.5 } }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <ErrorIcon color="error" fontSize="small" />
          <Typography variant="body2" fontWeight={500}>
            {failedRecords.length} failed record{failedRecords.length !== 1 ? "s" : ""}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
        <Stack divider={<Box sx={{ borderTop: 1, borderColor: "divider" }} />}>
          {failedRecords.map((record, i) => (
            <FailedRecordRow key={`failed-${schemaLabel}-${i}`} record={record} />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

interface FailedRecordRowProps {
  record: NonNullable<DocumentLoad["failed_records"]>[number];
}

const FailedRecordRow = ({ record }: FailedRecordRowProps) => {
  // Render only the data fields that have non-empty values — same suppression
  // rule as the loaded records list. Truncate to keep the row compact;
  // reviewers click into the source file for the full context.
  const populatedData = record.data
    ? Object.entries(record.data).filter(([, v]) => v !== null && v !== undefined && v !== "")
    : [];
  const dataPreview = populatedData
    .slice(0, 4)
    .map(([k, v]) => `${k}: ${typeof v === "string" || typeof v === "number" ? v : JSON.stringify(v)}`)
    .join(" · ");
  const truncated = populatedData.length > 4;

  return (
    <Box sx={{ py: 1 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.25 }}>
        <Chip
          label="Failed"
          size="small"
          color="error"
          variant="outlined"
          sx={{ minWidth: 84, fontWeight: 500, justifyContent: "center" }}
        />
        <Typography variant="body2" color="error.main" sx={{ flex: 1, minWidth: 0 }} title={record.error}>
          {record.error}
        </Typography>
      </Stack>
      {dataPreview.length > 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontFamily: "monospace", display: "block", wordBreak: "break-word", pl: 11 }}
        >
          {dataPreview}
          {truncated && " · …"}
        </Typography>
      )}
    </Box>
  );
};
